import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { escapeHtml } from '../../utils/escapeHtml';

interface OrderMapProps {
  latitude: number;
  longitude: number;
  address?: string;
  title?: string;
}

const OrderMap: React.FC<OrderMapProps> = ({ latitude, longitude, address, title }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    let mapInstance: any = null;
    let isMounted = true;

    const initializeMap = async () => {
      if (!latitude || !longitude) {
        return; // Не инициализируем карту, если координаты недоступны
      }

      if (typeof (window as any).ymaps === 'undefined') {
        // Загружаем скрипт Яндекс.Карт, если он еще не загружен
        const script = document.createElement('script');
        const ymapsApiKey = import.meta.env.VITE_YANDEX_MAPS_API_KEY || "";
        script.src = `https://api-maps.yandex.ru/2.1/?apikey=${ymapsApiKey}&lang=ru_RU`;
        script.async = true;

        script.onload = () => {
          if (isMounted && (window as any).ymaps) {
            (window as any).ymaps.ready(initYandexMap);
          }
        };

        script.onerror = () => {
          if (isMounted) {
            console.error('Failed to load Yandex Maps API');
          }
        };

        document.head.appendChild(script);
      } else {
        initYandexMap();
      }
    };

    const initYandexMap = () => {
      if (mapRef.current && latitude && longitude) {
        try {
          // Уничтожаем предыдущую карту, если она существует
          if ((window as any).ymaps && mapInstance) {
            mapInstance.destroy();
          }

          // Создаем новую карту без указания типа (используется тип по умолчанию - схема)
          mapInstance = new (window as any).ymaps.Map(mapRef.current, {
            center: [latitude, longitude],
            zoom: 16,
            controls: ['zoomControl', 'fullscreenControl', 'typeSelector']
          });

          // Добавляем метку
          const placemark = new (window as any).ymaps.Placemark(
            [latitude, longitude],
            {
              hintContent: escapeHtml(address) || t('order_location'),
              balloonContent: escapeHtml(address) || t('order_location'),
            },
            {
              preset: 'islands#redDotIcon',
            }
          );

          if (isMounted) {
            mapInstance.geoObjects.add(placemark);
          }
        } catch (error) {
          console.error('Error initializing Yandex Map:', error);
        }
      }
    };

    initializeMap();

    // Очищаем при размонтировании компонента
    return () => {
      isMounted = false;
      if (mapInstance) {
        mapInstance.destroy();
      }
    };
  }, [latitude, longitude, address, t]);

  if (!latitude || !longitude) {
    return (
      <Box sx={{ width: '100%', height: '300px', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          {t('coordinates_unavailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '300px', position: 'relative' }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <Box
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      />
    </Box>
  );
};

export default OrderMap;